import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SiteConfigProvider } from "./contexts/SiteConfigContext";
import { Toaster } from "./components/ui/sonner";

// Layouts
import Layout from "./components/Layout";
import AdminLayout from "./pages/admin/AdminLayout";

// Public Pages
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CarsPage from "./pages/CarsPage";
import CarDetailPage from "./pages/CarDetailPage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";

// Admin Pages
import LoginPage from "./pages/admin/LoginPage";
import DashboardPage from "./pages/admin/DashboardPage";
import AdminProductsPage from "./pages/admin/AdminProductsPage";
import AdminCarsPage from "./pages/admin/AdminCarsPage";
import AdminOrdersPage from "./pages/admin/AdminOrdersPage";
import AdminMessagesPage from "./pages/admin/AdminMessagesPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";

// Protected Route Component
function ProtectedRoute({ children }) {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-mmd-dark-bg">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-mmd-gold"></div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/administration" replace />;
    }
    
    return children;
}

// Admin Route Guard
function AdminRouteGuard({ children }) {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-mmd-dark-bg">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-mmd-gold"></div>
            </div>
        );
    }
    
    if (isAuthenticated) {
        return <Navigate to="/administration/dashboard" replace />;
    }
    
    return children;
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            <Route path="/materiel" element={<Layout><ProductsPage /></Layout>} />
            <Route path="/materiel/:id" element={<Layout><ProductDetailPage /></Layout>} />
            <Route path="/voitures" element={<Layout><CarsPage /></Layout>} />
            <Route path="/voitures/:id" element={<Layout><CarDetailPage /></Layout>} />
            <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
            <Route path="/a-propos" element={<Layout><AboutPage /></Layout>} />

            {/* Admin Login */}
            <Route path="/administration" element={
                <AdminRouteGuard>
                    <LoginPage />
                </AdminRouteGuard>
            } />

            {/* Protected Admin Routes */}
            <Route path="/administration" element={
                <ProtectedRoute>
                    <AdminLayout />
                </ProtectedRoute>
            }>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="products" element={<AdminProductsPage />} />
                <Route path="cars" element={<AdminCarsPage />} />
                <Route path="orders" element={<AdminOrdersPage />} />
                <Route path="messages" element={<AdminMessagesPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
            </Route>

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <ThemeProvider>
            <SiteConfigProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <AppRoutes />
                        <Toaster position="top-right" richColors />
                    </BrowserRouter>
                </AuthProvider>
            </SiteConfigProvider>
        </ThemeProvider>
    );
}

export default App;
